// Thin PlazaStack storefront client. Used server-side (catalog, with Next's
// fetch cache + the `catalog` tag) and browser-side (checkout, success poll).
//
// Config: NEXT_PUBLIC_PLAZASTACK_URL / NEXT_PUBLIC_PLAZASTACK_TOKEN come from
// wrangler.jsonc vars at build time. The token is PUBLISHABLE (like a Stripe
// pk_ key; API-CONTRACT 2.1), so it is also compiled in as a fallback: a
// missing build variable must never be able to break checkout.
//
// Resilience (same rules as SneakerGang's lib/pag-commerce/client.ts):
//   - GETs retry on network errors and 5xx only, 4 s per-attempt timeout,
//     never on 4xx (429 especially).
//   - POST /checkout is never retried or aborted: it reserves stock and
//     creates a Stripe session, so a retry could double-reserve.

import { PsError } from './types';
import type {
  PsCheckoutCreated, PsCheckoutInput, PsCheckoutSession,
  PsCollection, PsPage, PsProduct,
} from './types';

export const PLAZASTACK_URL =
  (process.env.NEXT_PUBLIC_PLAZASTACK_URL || 'https://app.plazastack.com').replace(/\/+$/, '');
export const PLAZASTACK_TOKEN =
  process.env.NEXT_PUBLIC_PLAZASTACK_TOKEN || 'pst_TyJ3n7otpm-Xz0sxyG7JfGQU';
export const PLAZASTACK_TENANT = 'noir-petale';

const BASE = `${PLAZASTACK_URL}/api/v1/${PLAZASTACK_TENANT}`;

const RETRY_DELAYS_MS = [250, 750];
const ATTEMPT_TIMEOUT_MS = 4_000;

type NextInit = RequestInit & { next?: { revalidate?: number; tags?: string[] } };

async function getJson<T>(path: string, init?: NextInit): Promise<{ status: number; json: T | null }> {
  for (let attempt = 0; ; attempt++) {
    const canRetry = attempt < RETRY_DELAYS_MS.length;
    let res: Response | null = null;
    try {
      const signal = typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function'
        ? AbortSignal.timeout(ATTEMPT_TIMEOUT_MS)
        : undefined;
      res = await fetch(`${BASE}${path}`, {
        ...init,
        headers: { 'X-Storefront-Token': PLAZASTACK_TOKEN, Accept: 'application/json', ...(init?.headers || {}) },
        signal,
      });
    } catch (err) {
      if (!canRetry) throw err;
    }
    if (res) {
      if (res.status < 500 || !canRetry) {
        let json: T | null = null;
        try { json = (await res.json()) as T; } catch { json = null; }
        return { status: res.status, json };
      }
      try { void res.body?.cancel().catch(() => {}); } catch { /* best effort */ }
    }
    await new Promise((r) => setTimeout(r, RETRY_DELAYS_MS[attempt]));
  }
}

function toError(status: number, json: unknown): PsError {
  const e = (json as { error?: { code?: string; message?: string; details?: unknown } } | null)?.error;
  return new PsError(status, e?.code ?? (status === 429 ? 'rate_limited' : 'internal'), e?.message ?? `HTTP ${status}`, e?.details);
}

/** Drain every page of the active catalog. Throws on any failure (never returns a partial list). */
export async function fetchAllProducts(opts?: { revalidate?: number; tags?: string[] }): Promise<PsProduct[]> {
  const out: PsProduct[] = [];
  let cursor: string | undefined;
  for (let i = 0; i < 20; i++) {
    const qs = new URLSearchParams({ limit: '100' });
    if (cursor) qs.set('cursor', cursor);
    const { status, json } = await getJson<PsPage<PsProduct>>(`/products?${qs}`, {
      next: { revalidate: opts?.revalidate ?? 60, tags: opts?.tags ?? ['catalog'] },
    });
    if (status < 200 || status >= 300 || !json) throw toError(status, json);
    out.push(...json.data);
    if (!json.page?.has_more || !json.page.next_cursor) return out;
    cursor = json.page.next_cursor;
  }
  throw new PsError(500, 'internal', 'catalog pagination did not terminate');
}

export async function fetchCollections(opts?: { revalidate?: number; tags?: string[] }): Promise<PsCollection[]> {
  const { status, json } = await getJson<PsPage<PsCollection>>(`/collections?limit=100`, {
    next: { revalidate: opts?.revalidate ?? 60, tags: opts?.tags ?? ['catalog'] },
  });
  if (status < 200 || status >= 300 || !json) throw toError(status, json);
  return json.data;
}

/** POST /checkout. No retry, no abort. Throws PsError with the API code. */
export async function createCheckout(input: PsCheckoutInput, idempotencyKey?: string): Promise<PsCheckoutCreated> {
  const res = await fetch(`${BASE}/checkout`, {
    method: 'POST',
    headers: {
      'X-Storefront-Token': PLAZASTACK_TOKEN,
      'Content-Type': 'application/json',
      ...(idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}),
    },
    body: JSON.stringify(input),
  });
  let json: unknown = null;
  try { json = await res.json(); } catch { json = null; }
  if (res.status < 200 || res.status >= 300) throw toError(res.status, json);
  return json as PsCheckoutCreated;
}

export async function getCheckoutSession(sessionId: string): Promise<PsCheckoutSession> {
  const { status, json } = await getJson<PsCheckoutSession>(`/checkout/${encodeURIComponent(sessionId)}`, { cache: 'no-store' });
  if (status < 200 || status >= 300 || !json) throw toError(status, json);
  return json;
}

/** Variant ids that currently can't be bought, from an insufficient_stock 409. */
export function insufficientStockVariantIds(err: unknown): string[] {
  if (!(err instanceof PsError) || err.code !== 'insufficient_stock') return [];
  const items = (err.details as { items?: { variant_id?: string }[] } | undefined)?.items;
  return (items ?? []).map((i) => i.variant_id).filter((x): x is string => !!x);
}
